import type { MealCondition, MealMetric } from "@/lib/schema";
import { orderedConditions } from "./conditions";

type DensityVerdict = "good" | "bad";

export interface ConditionDensity {
  condition: MealCondition;
  verdict: DensityVerdict;
  // Signed distance from the condition's density reference: 0 at the
  // reference, positive above it (denser), negative below. Whole percent.
  deviationPercent: number;
}

// The strictest kcal budget among the instance's kcal conditions: the
// smallest "høyst" threshold, or the largest "minst" one when only those
// exist. null without any kcal condition — density has no reference then.
function strictestKcalBudget(conditions: MealCondition[]): number | null {
  const kcal = conditions.filter((c) => c.metric === "kcal");
  const atMost = kcal.filter((c) => c.direction === "atMost");
  if (atMost.length > 0) return Math.min(...atMost.map((c) => c.threshold));
  if (kcal.length > 0) return Math.max(...kcal.map((c) => c.threshold));
  return null;
}

// Density verdicts for one metric column against one meal's (or the day's
// summed) amounts. Empty for the kcal column (kcal only supplies the
// denominator), without a kcal condition, and at 0 kcal (density undefined).
export function densityVerdicts(
  conditions: MealCondition[],
  metric: MealMetric,
  amounts: Record<MealMetric, number>,
): ConditionDensity[] {
  if (metric === "kcal") return [];
  const budget = strictestKcalBudget(conditions);
  if (budget === null || amounts.kcal <= 0) return [];
  // One column holds one metric, so only the within-group half of
  // Betingelsesrekkefølge can bite here: Minst before Høyst, lenient first.
  return orderedConditions(conditions.filter((c) => c.metric === metric)).map(
    (condition) => {
      // amount/kcal vs threshold/budget, cross-multiplied; boundaries
      // inclusive like condition thresholds.
      const density = amounts[metric] * budget;
      const reference = condition.threshold * amounts.kcal;
      const good =
        condition.direction === "atLeast"
          ? density >= reference
          : density <= reference;
      return {
        condition,
        verdict: good ? ("good" as const) : ("bad" as const),
        deviationPercent: Math.round((density / reference - 1) * 100),
      };
    },
  );
}
